/**
 * SDK HMAC signing for the chat-flow transport, ported from the customer widget
 * (apps/chat-v1 `api/client.ts`). The console cannot import that app, so the recipe
 * is duplicated here — and it is a wire contract with the gateway's web-sdk auth,
 * so the https->http swap, the encodeURI calls and the header names are all load
 * bearing rather than leftovers.
 *
 *   SIGNATURE = base64(HMAC-SHA256(url + METHOD + clientType + timestamp, appKey))
 *   headers:    APP-ID / SIGNATURE / TIMESTAMP / CLIENT-TYPE
 *
 * Signing runs on Web Crypto instead of the widget's crypto-js so the console
 * takes no new dependency; that makes it async, which every caller already is.
 */

export interface FlowSignaturePayloadInput {
  url: string
  method: string
  clientType: string
  region: string
  timestamp: number
}

export interface FlowSignedHeadersInput extends FlowSignaturePayloadInput {
  appId: string
  appKey: string
}

export const buildFlowSignaturePayload = ({
  url,
  method,
  clientType,
  region,
  timestamp,
}: FlowSignaturePayloadInput): string => {
  const signedUrl = url.replace('https', 'http').replace('{{region}}', region)
  return (
    signedUrl +
    encodeURI(method.toUpperCase()) +
    encodeURI(clientType) +
    encodeURI(String(timestamp))
  )
}

const toBase64 = (signature: ArrayBuffer): string => {
  let binary = ''
  for (const byte of new Uint8Array(signature)) binary += String.fromCharCode(byte)
  return btoa(binary)
}

// TextEncoder is typed against ArrayBufferLike, which Web Crypto's BufferSource
// does not accept; the bytes are the same either way.
const utf8 = (value: string): Uint8Array<ArrayBuffer> =>
  new TextEncoder().encode(value) as Uint8Array<ArrayBuffer>

export const signFlowPayload = async (payload: string, appKey: string): Promise<string> => {
  const key = await crypto.subtle.importKey(
    'raw',
    utf8(appKey),
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign'],
  )
  return toBase64(await crypto.subtle.sign('HMAC', key, utf8(payload)))
}

export const buildFlowSignedHeaders = async ({
  appId,
  appKey,
  ...payloadInput
}: FlowSignedHeadersInput): Promise<Record<string, string>> => ({
  'APP-ID': appId,
  'CLIENT-TYPE': payloadInput.clientType,
  SIGNATURE: await signFlowPayload(buildFlowSignaturePayload(payloadInput), appKey),
  TIMESTAMP: String(payloadInput.timestamp),
})
